// Isometric workstations for digital humanoid workers, drawn in code.
// One tile is 1 x 1 world units; u is half a tile's screen width. The tile centre sits at (0, 0).
export const KINDS = ["laptop", "dual", "triple", "call", "tower", "lounge-call", "lounge-tablet"];
export const BOX = { left: -1.3, top: -2.05, width: 2.6, height: 2.75 };
export const FRAMES = 4;

const INK = "#191919";
const ZS = 1.25;
const PERIOD = 1.6;

const TASKS = {
  laptop: ["Drafting outreach", "Answering support", "Writing the brief"],
  dual: ["Qualifying leads", "Reconciling invoices", "Updating the CRM"],
  triple: ["Leading the standup", "Running the forecast", "Negotiating a deal"],
  call: ["On a call with a customer", "Booking a meeting", "Closing a renewal"],
  tower: ["Shipping a release", "Running payroll", "Crunching the numbers"],
  "lounge-call": ["Interviewing a candidate", "On a partner call", "Handling an escalation"],
  "lounge-tablet": ["Reviewing contracts", "Approving expenses", "Reading the market"],
};
export const taskFor = (kind, n) => TASKS[kind][Math.floor(n * TASKS[kind].length) % TASKS[kind].length];

function poly(g, pts, fill, stroke, line) {
  g.beginPath();
  g.moveTo(pts[0][0], pts[0][1]);
  for (let k = 1; k < pts.length; k += 1) g.lineTo(pts[k][0], pts[k][1]);
  g.closePath();
  if (fill) {
    g.fillStyle = fill;
    g.fill();
  }
  if (stroke) {
    g.strokeStyle = stroke;
    g.lineWidth = line;
    g.stroke();
  }
}

function makeScene(g, u, mirror = false) {
  // mirror flips the station along X, so its worker faces the viewer instead of turning away.
  const P = mirror
    ? (x, y, z = 0) => [(-x - y) * u, (-x + y) * u * 0.5 - z * u * ZS]
    : (x, y, z = 0) => [(x - y) * u, (x + y) * u * 0.5 - z * u * ZS];
  const line = Math.max(0.6, u * 0.016);
  const box = (x, y, z, dx, dy, dz, { top = "#fff", left = "#efefef", right = "#e2e2e2", stroke = INK } = {}) => {
    const x1 = x + dx;
    const y1 = y + dy;
    const z1 = z + dz;
    const xf = mirror ? x : x1;
    poly(g, [P(xf, y, z), P(xf, y1, z), P(xf, y1, z1), P(xf, y, z1)], right, stroke, line);
    poly(g, [P(x, y1, z), P(x1, y1, z), P(x1, y1, z1), P(x, y1, z1)], left, stroke, line);
    poly(g, [P(x, y, z1), P(x1, y, z1), P(x1, y1, z1), P(x, y1, z1)], top, stroke, line);
  };
  const seg = (a, b, width, core = "#fff", edge = INK) => {
    g.beginPath();
    g.moveTo(a[0], a[1]);
    g.lineTo(b[0], b[1]);
    g.lineCap = "round";
    g.strokeStyle = edge;
    g.lineWidth = width;
    g.stroke();
    if (core) {
      g.strokeStyle = core;
      g.lineWidth = Math.max(0.4, width - line * 2);
      g.stroke();
    }
  };
  const limb = (pts, width, core) => {
    for (let k = 1; k < pts.length; k += 1) seg(pts[k - 1], pts[k], width, core);
  };
  const dot = (p, r, fill, stroke) => {
    g.beginPath();
    g.arc(p[0], p[1], r, 0, Math.PI * 2);
    if (fill) {
      g.fillStyle = fill;
      g.fill();
    }
    if (stroke) {
      g.strokeStyle = stroke;
      g.lineWidth = line;
      g.stroke();
    }
  };
  // Draws unit-square content onto a quad: tl, tr, bl.
  const onQuad = (tl, tr, bl, draw) => {
    g.save();
    g.transform(tr[0] - tl[0], tr[1] - tl[1], bl[0] - tl[0], bl[1] - tl[1], tl[0], tl[1]);
    draw();
    g.restore();
  };
  return { P, u, line, box, seg, limb, dot, onQuad, mirror };
}

// Screen content, drawn in a unit square.
const CONTENT = {
  chart(g, ph) {
    g.fillStyle = "rgba(25,25,25,0.85)";
    g.fillRect(0.08, 0.1, 0.34, 0.07);
    g.fillStyle = "rgba(25,25,25,0.18)";
    g.fillRect(0.08, 0.24, 0.84, 0.012);
    for (let b = 0; b < 7; b += 1) {
      const hgt = 0.12 + (((b * 37 + Math.floor(ph * 4) * (b + 3) * 11) % 10) / 10) * 0.42;
      g.fillStyle = b === 6 ? INK : "rgba(25,25,25,0.3)";
      g.fillRect(0.1 + b * 0.115, 0.88 - hgt, 0.07, hgt);
    }
  },
  list(g, ph) {
    g.fillStyle = "rgba(25,25,25,0.85)";
    g.fillRect(0.08, 0.1, 0.4, 0.07);
    const active = Math.floor(ph * 4) % 4;
    for (let r = 0; r < 4; r += 1) {
      const y = 0.28 + r * 0.16;
      if (r === active) {
        g.fillStyle = "rgba(25,25,25,0.1)";
        g.fillRect(0.05, y - 0.04, 0.9, 0.14);
      }
      g.fillStyle = "rgba(25,25,25,0.55)";
      g.beginPath();
      g.arc(0.14, y + 0.03, 0.035, 0, Math.PI * 2);
      g.fill();
      g.fillStyle = "rgba(25,25,25,0.4)";
      g.fillRect(0.24, y, 0.42 - r * 0.05, 0.05);
      g.fillStyle = r === active ? INK : "rgba(25,25,25,0.25)";
      g.fillRect(0.76, y, 0.14, 0.05);
    }
  },
  code(g, ph) {
    const lines = [0.5, 0.34, 0.62, 0.28, 0.44, 0.56];
    const cursor = Math.floor(ph * 4) % lines.length;
    lines.forEach((len, r) => {
      const indent = [0, 0.08, 0.08, 0.16, 0.08, 0][r];
      g.fillStyle = r === cursor ? INK : "rgba(25,25,25,0.35)";
      g.fillRect(0.1 + indent, 0.14 + r * 0.12, r === cursor ? len * (0.4 + ph * 0.6) : len, 0.05);
    });
  },
  video(g, ph) {
    const speaker = Math.floor(ph * 4) % 4;
    for (let k = 0; k < 4; k += 1) {
      const x = 0.06 + (k % 2) * 0.46;
      const y = 0.08 + Math.floor(k / 2) * 0.44;
      g.fillStyle = k === speaker ? "rgba(25,25,25,0.16)" : "rgba(25,25,25,0.07)";
      g.fillRect(x, y, 0.42, 0.4);
      g.fillStyle = "rgba(25,25,25,0.5)";
      g.beginPath();
      g.arc(x + 0.21, y + 0.16, 0.06, 0, Math.PI * 2);
      g.fill();
      g.fillRect(x + 0.12, y + 0.25, 0.18, 0.1);
      if (k === speaker) {
        g.fillStyle = INK;
        g.fillRect(x, y + 0.38, 0.42, 0.02);
      }
    }
  },
  calendar(g, ph) {
    g.fillStyle = "rgba(25,25,25,0.85)";
    g.fillRect(0.08, 0.1, 0.3, 0.07);
    const booked = 3 + (Math.floor(ph * 4) % 4);
    for (let k = 0; k < 15; k += 1) {
      const x = 0.08 + (k % 5) * 0.17;
      const y = 0.28 + Math.floor(k / 5) * 0.2;
      g.fillStyle = [1, 4, 7, 9, 12, 13, 2].indexOf(k) > -1 && [1, 4, 7, 9, 12, 13, 2].indexOf(k) < booked ? INK : "rgba(25,25,25,0.12)";
      g.fillRect(x, y, 0.14, 0.16);
    }
  },
  browser(g, ph) {
    g.fillStyle = "rgba(25,25,25,0.08)";
    g.fillRect(0, 0, 1, 0.15);
    for (let k = 0; k < 3; k += 1) {
      g.fillStyle = "rgba(25,25,25,0.35)";
      g.beginPath();
      g.arc(0.06 + k * 0.045, 0.075, 0.016, 0, Math.PI * 2);
      g.fill();
    }
    g.fillStyle = "rgba(25,25,25,0.14)";
    g.fillRect(0.22, 0.045, 0.62, 0.06);
    const scroll = ph * 0.18;
    g.fillStyle = "rgba(25,25,25,0.85)";
    g.fillRect(0.08, 0.24 - scroll, 0.48, 0.08);
    g.fillStyle = "rgba(25,25,25,0.3)";
    [0.38, 0.46, 0.54].forEach((y, k) => g.fillRect(0.08, y - scroll, 0.46 - k * 0.08, 0.035));
    g.fillStyle = "rgba(25,25,25,0.12)";
    g.fillRect(0.62, 0.24 - scroll, 0.3, 0.34);
    g.fillStyle = INK;
    g.fillRect(0.08, 0.66 - scroll, 0.18, 0.08);
    g.fillStyle = "rgba(25,25,25,0.12)";
    [0.1, 0.4, 0.7].forEach((x) => g.fillRect(x, 0.86 - scroll, 0.24, 0.2));
  },
  chat(g, ph) {
    const rows = 2 + (Math.floor(ph * 4) % 3);
    for (let r = 0; r < rows; r += 1) {
      const mine = r % 2 === 1;
      const width = [0.52, 0.4, 0.6, 0.34][r];
      g.fillStyle = mine ? "rgba(25,25,25,0.85)" : "rgba(25,25,25,0.12)";
      g.beginPath();
      if (g.roundRect) g.roundRect(mine ? 0.92 - width : 0.08, 0.1 + r * 0.21, width, 0.14, 0.05);
      else g.rect(mine ? 0.92 - width : 0.08, 0.1 + r * 0.21, width, 0.14);
      g.fill();
    }
  },
  docs(g, ph) {
    g.fillStyle = "rgba(25,25,25,0.85)";
    g.fillRect(0.1, 0.12, 0.5, 0.08);
    const lines = [0.8, 0.76, 0.8, 0.52, 0.78, 0.66];
    const done = 2 + Math.floor(ph * 4);
    lines.forEach((len, r) => {
      g.fillStyle = r < done ? "rgba(25,25,25,0.35)" : "rgba(25,25,25,0.1)";
      g.fillRect(0.1, 0.3 + r * 0.1, len * (r === done - 1 ? 0.5 + ph * 0.5 : 1), 0.035);
    });
  },
  mail(g, ph) {
    const open = Math.floor(ph * 4) % 3;
    for (let r = 0; r < 3; r += 1) {
      g.fillStyle = r === open ? "rgba(25,25,25,0.12)" : "rgba(25,25,25,0.05)";
      g.fillRect(0.06, 0.1 + r * 0.28, 0.88, 0.24);
      g.fillStyle = "rgba(25,25,25,0.6)";
      g.fillRect(0.12, 0.16 + r * 0.28, 0.3, 0.05);
      g.fillStyle = "rgba(25,25,25,0.3)";
      g.fillRect(0.12, 0.25 + r * 0.28, 0.66, 0.035);
    }
  },
};

// A flat monitor facing the worker (+X), centred on (x, y), rotated by angle around the vertical axis.
function monitor(s, g, x, y, width, content, ph, angle = 0, z0 = 0.47, arm = false) {
  const { P, line, seg, onQuad } = s;
  const wy = Math.cos(angle);
  const wx = Math.sin(angle);
  const half = width / 2;
  const zb = z0 + 0.13;
  const zt = zb + width * 0.62;
  const corner = (side, z) => P(x + wx * half * side, y + wy * half * side, z);
  seg(P(x - 0.02, y, arm ? 0.47 : z0), P(x - 0.02, y, zb + 0.02), Math.max(1.4, s.u * (arm ? 0.028 : 0.035)), "#d8d8d8");
  g.beginPath();
  const base = P(x - 0.02, y, arm ? 0.47 : z0);
  g.ellipse(base[0], base[1], s.u * (arm ? 0.05 : 0.1), s.u * (arm ? 0.022 : 0.045), 0, 0, Math.PI * 2);
  g.fillStyle = "#e6e6e6";
  g.fill();
  g.strokeStyle = INK;
  g.lineWidth = line;
  g.stroke();
  const tl = corner(1, zt);
  const tr = corner(-1, zt);
  const br = corner(-1, zb);
  const bl = corner(1, zb);
  poly(g, [tl, tr, br, bl], INK, INK, line);
  if (s.mirror) {
    poly(g, [tl, tr, br, bl], "#2e2e2e", INK, line);
    return [tl, tr, br, bl];
  }
  const inset = (a, b, c, k) => [a[0] + (b[0] - a[0]) * k + (c[0] - a[0]) * k * 1.6, a[1] + (b[1] - a[1]) * k + (c[1] - a[1]) * k * 1.6];
  const stl = inset(tl, tr, bl, 0.04);
  const str = inset(tr, tl, br, 0.04);
  const sbl = inset(bl, br, tl, 0.04);
  const sbr = inset(br, bl, tr, 0.04);
  poly(g, [stl, str, sbr, sbl], "#ffffff");
  onQuad(stl, str, sbl, () => CONTENT[content](g, ph));
  return [stl, str, sbr, sbl];
}

function laptop(s, g, x, y, content, ph) {
  const { P, box, line, onQuad } = s;
  box(x - 0.1, y - 0.14, 0.47, 0.2, 0.28, 0.018, { top: "#f4f4f4" });
  const tl = P(x - 0.15, y + 0.14, 0.73);
  const tr = P(x - 0.15, y - 0.14, 0.73);
  const br = P(x - 0.1, y - 0.14, 0.488);
  const bl = P(x - 0.1, y + 0.14, 0.488);
  poly(g, [tl, tr, br, bl], INK, INK, line);
  if (s.mirror) {
    const mid = [(tl[0] + br[0]) / 2, (tl[1] + br[1]) / 2];
    s.dot(mid, Math.max(1, s.u * 0.02), ph < 0.5 ? "#ffffff" : "rgba(255,255,255,0.4)");
    return [tl, tr, br, bl];
  }
  const k = 0.07;
  const lerp2 = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  const stl = lerp2(lerp2(tl, tr, k), lerp2(bl, br, k), k);
  const str = lerp2(lerp2(tr, tl, k), lerp2(br, bl, k), k);
  const sbl = lerp2(lerp2(bl, br, k), lerp2(tl, tr, k), k);
  const sbr = lerp2(lerp2(br, bl, k), lerp2(tr, tl, k), k);
  poly(g, [stl, str, sbr, sbl], "#fff");
  onQuad(stl, str, sbl, () => CONTENT[content](g, ph));
  return [stl, str, sbr, sbl];
}

const DESK = { top: "#ffffff", left: "#f1f1f1", right: "#e6e6e6" };
const SEAT = { top: "#ededed", left: "#e0e0e0", right: "#d3d3d3" };

function desk(s, withTower) {
  const { box } = s;
  box(-0.47, -0.44, 0, 0.36, 0.03, 0.44, DESK);
  if (withTower) box(-0.43, 0.24, 0, 0.15, 0.13, 0.33, { top: "#4a4a4a", left: "#333", right: "#2a2a2a" });
  box(-0.48, -0.44, 0.44, 0.38, 0.88, 0.03, DESK);
}

function deskFront(s) {
  s.box(-0.47, 0.41, 0, 0.36, 0.03, 0.44, DESK);
}

function chair(s, backOnly) {
  const { box, seg, P, dot, u } = s;
  if (!backOnly) {
    const hub = P(0.22, 0, 0.05);
    for (let k = 0; k < 5; k += 1) {
      const a = (k / 5) * Math.PI * 2 + 0.4;
      const foot = P(0.22 + Math.cos(a) * 0.15, Math.sin(a) * 0.15, 0.03);
      seg(hub, foot, Math.max(1, u * 0.028), null);
      dot(foot, Math.max(0.8, u * 0.018), INK);
    }
    seg(P(0.22, 0, 0.05), P(0.22, 0, 0.28), Math.max(1.2, u * 0.032), "#bdbdbd");
    box(0.08, -0.14, 0.28, 0.28, 0.28, 0.045, SEAT);
    return;
  }
  seg(P(0.37, 0, 0.33), P(0.37, 0, 0.42), Math.max(1.2, u * 0.03), "#bdbdbd");
  box(0.35, -0.12, 0.42, 0.03, 0.24, 0.18, SEAT);
}

function head(s, g, c, facing, ph, extra) {
  const { u, line } = s;
  const rx = u * 0.118;
  const ry = u * 0.138;
  g.beginPath();
  g.ellipse(c[0], c[1], rx, ry, 0, 0, Math.PI * 2);
  g.fillStyle = s.human ? INK : "#fff";
  g.fill();
  if (s.human) return;
  g.save();
  g.clip();
  g.fillStyle = "#ececec";
  g.fillRect(c[0] + rx * 0.3, c[1] - ry, rx, ry * 2);
  if (facing === "toward") {
    // Three-quarter view: the visor wraps round toward the viewer.
    g.fillStyle = INK;
    g.beginPath();
    if (g.roundRect) g.roundRect(c[0] - rx * 0.25, c[1] - ry * 0.32, rx * 1.35, ry * 0.46, ry * 0.23);
    else g.rect(c[0] - rx * 0.25, c[1] - ry * 0.32, rx * 1.35, ry * 0.46);
    g.fill();
    g.fillStyle = "rgba(255,255,255,0.85)";
    g.fillRect(c[0] - rx * 0.05 + rx * 0.7 * ph, c[1] - ry * 0.24, rx * 0.22, ry * 0.24);
  } else if (facing === "away") {
    // The visor wraps round the far side: only its edge shows.
    g.fillStyle = INK;
    g.beginPath();
    g.ellipse(c[0] - rx * 0.95, c[1] - ry * 0.12, rx * 0.4, ry * 0.26, 0, 0, Math.PI * 2);
    g.fill();
  } else {
    g.fillStyle = INK;
    g.beginPath();
    if (g.roundRect) g.roundRect(c[0] - rx * 0.8, c[1] - ry * 0.34, rx * 1.6, ry * 0.46, ry * 0.23);
    else g.rect(c[0] - rx * 0.8, c[1] - ry * 0.34, rx * 1.6, ry * 0.46);
    g.fill();
    g.fillStyle = extra === "glow" ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.85)";
    g.fillRect(c[0] - rx * 0.6 + rx * 0.9 * ph, c[1] - ry * 0.26, rx * 0.26, ry * 0.26);
  }
  g.restore();
  g.beginPath();
  g.ellipse(c[0], c[1], rx, ry, 0, 0, Math.PI * 2);
  g.strokeStyle = INK;
  g.lineWidth = line;
  g.stroke();
  const ear = [c[0] + (facing === "away" ? rx * 0.35 : facing === "toward" ? -rx * 0.55 : rx * 0.92), c[1] + ry * 0.05];
  s.dot(ear, rx * 0.2, "#e6e6e6", INK);
}

function speech(s, g, c, ph, dir = -1) {
  const { u } = s;
  for (let k = 0; k < 3; k += 1) {
    const q = (((ph + k / 3) % 1) + 1) % 1;
    g.beginPath();
    g.arc(c[0] + dir * u * 0.12, c[1] - u * 0.05, u * (0.1 + q * 0.22), dir < 0 ? Math.PI * 0.8 : -Math.PI * 0.2, dir < 0 ? Math.PI * 1.2 : Math.PI * 0.2);
    g.strokeStyle = `rgba(25,25,25,${0.6 * (1 - q)})`;
    g.lineWidth = Math.max(1, u * 0.022);
    g.stroke();
  }
}

// A worker seated at a desk facing -X, seen from behind and to the right.
function deskWorker(s, g, kind, ph) {
  const { P, limb, u, seg } = s;
  const typing = Math.sin(ph * Math.PI * 4) * 0.012;
  const call = kind === "call";
  const headset = kind === "triple";
  const w = u * 0.08;
  // Far arm (behind the torso)
  limb([P(0.24, -0.13, 0.64), P(0.1, -0.18, 0.5), P(-0.14, -0.08, 0.5 + typing)], w, "#ececec");
  // Legs under the desk
  limb([P(0.22, -0.07, 0.34), P(0.0, -0.08, 0.36), P(-0.01, -0.08, 0.04)], w * 1.05, "#ececec");
  limb([P(0.22, 0.07, 0.34), P(0.0, 0.08, 0.36), P(-0.01, 0.08, 0.04)], w * 1.05);
  // Torso
  const sl = P(0.25, -0.13, 0.66);
  const sr = P(0.25, 0.13, 0.66);
  const hl = P(0.25, -0.085, 0.36);
  const hr = P(0.25, 0.085, 0.36);
  g.beginPath();
  g.moveTo(sl[0], sl[1]);
  g.quadraticCurveTo((sl[0] + sr[0]) / 2, Math.min(sl[1], sr[1]) - u * 0.05, sr[0], sr[1]);
  g.quadraticCurveTo(sr[0] + u * 0.02, (sr[1] + hr[1]) / 2, hr[0], hr[1]);
  g.lineTo(hl[0], hl[1]);
  g.quadraticCurveTo(sl[0] - u * 0.02, (sl[1] + hl[1]) / 2, sl[0], sl[1]);
  g.fillStyle = "#fff";
  g.fill();
  g.strokeStyle = INK;
  g.lineWidth = s.line;
  g.stroke();
  g.beginPath();
  const spine1 = P(0.25, 0, 0.62);
  const spine2 = P(0.25, 0, 0.42);
  g.moveTo(spine1[0], spine1[1]);
  g.lineTo(spine2[0], spine2[1]);
  g.strokeStyle = "rgba(25,25,25,0.22)";
  g.stroke();
  seg(P(0.24, 0, 0.66), P(0.23, 0, 0.73), u * 0.05, "#e6e6e6");
  const c = P(0.22, 0, 0.83);
  head(s, g, c, s.mirror ? "toward" : "away", ph);
  if (headset) {
    g.beginPath();
    g.ellipse(c[0], c[1] - u * 0.02, u * 0.13, u * 0.15, 0, Math.PI * 1.05, Math.PI * 1.95);
    g.strokeStyle = INK;
    g.lineWidth = Math.max(1.2, u * 0.03);
    g.stroke();
    speech(s, g, c, ph, -1);
  }
  // Near arm
  if (call) {
    limb([P(0.25, 0.13, 0.64), P(0.22, 0.22, 0.56), P(0.21, 0.11, 0.8)], w);
    const hand = P(0.21, 0.1, 0.81);
    g.save();
    g.translate(hand[0], hand[1]);
    g.rotate(-0.35);
    g.fillStyle = INK;
    g.beginPath();
    if (g.roundRect) g.roundRect(-u * 0.03, -u * 0.1, u * 0.06, u * 0.16, u * 0.015);
    else g.rect(-u * 0.03, -u * 0.1, u * 0.06, u * 0.16);
    g.fill();
    g.restore();
    speech(s, g, c, ph, -1);
  } else limb([P(0.25, 0.13, 0.64), P(0.12, 0.19, 0.5), P(-0.14, 0.08, 0.5 - typing)], w);
}

function deskItems(s, g, kind, ph) {
  const { box, P, u, dot } = s;
  if (kind === "laptop") {
    laptop(s, g, -0.2, 0, "mail", ph);
    box(-0.3, 0.2, 0.47, 0.08, 0.05, 0.01, { top: INK, left: INK, right: INK });
    if (ph > 0.5) poly(g, [P(-0.29, 0.21, 0.482), P(-0.23, 0.21, 0.482), P(-0.23, 0.24, 0.482), P(-0.29, 0.24, 0.482)], "#fff");
    const mug = P(-0.34, -0.3, 0.47);
    g.fillStyle = "#fff";
    g.fillRect(mug[0] - u * 0.035, mug[1] - u * 0.09, u * 0.07, u * 0.09);
    g.strokeStyle = INK;
    g.lineWidth = s.line;
    g.strokeRect(mug[0] - u * 0.035, mug[1] - u * 0.09, u * 0.07, u * 0.09);
    return;
  }
  if (kind === "dual") {
    monitor(s, g, -0.4, 0.2, 0.34, "list", ph, 0.28);
    monitor(s, g, -0.4, -0.18, 0.34, "chart", ph, -0.28);
  } else if (kind === "triple") {
    monitor(s, g, -0.33, 0.33, 0.28, "code", ph, 0.75);
    monitor(s, g, -0.42, 0, 0.3, "video", ph);
    monitor(s, g, -0.33, -0.33, 0.28, "chart", ph, -0.75);
  } else if (kind === "call") {
    monitor(s, g, -0.4, 0.05, 0.38, "calendar", ph);
  } else if (kind === "tower") {
    monitor(s, g, -0.41, 0.12, 0.34, "code", ph, 0.2);
    laptop(s, g, -0.24, -0.26, "chart", (ph + 0.5) % 1);
    const led = P(-0.28, 0.33, 0.3);
    dot(led, Math.max(0.8, u * 0.014), ph < 0.5 ? "#ffffff" : "#777");
  }
  box(-0.22, -0.15, 0.47, 0.08, 0.3, 0.012, { top: "#f2f2f2" });
  if (kind !== "call") box(-0.2, 0.2, 0.47, 0.05, 0.035, 0.012, { top: "#f2f2f2" });
}

// A worker in a lounge chair facing the viewer: on a call, or on a tablet.
function loungeWorker(s, g, kind, ph) {
  const { box, P, limb, seg, u } = s;
  const legW = Math.max(1, u * 0.022);
  for (const [lx, ly] of [[0.12, 0.12], [0.12, -0.24], [-0.24, 0.12]]) seg(P(lx, ly, 0), P(lx, ly, 0.18), legW, null);
  box(-0.3, -0.28, 0.18, 0.08, 0.44, 0.4, SEAT);
  box(-0.26, -0.3, 0.18, 0.42, 0.06, 0.34, SEAT);
  box(-0.26, -0.26, 0.18, 0.42, 0.42, 0.12, SEAT);
  // Side table with a coffee cup
  seg(P(0.34, -0.36, 0), P(0.34, -0.36, 0.3), Math.max(1, u * 0.024), null);
  const top = P(0.34, -0.36, 0.3);
  g.beginPath();
  g.ellipse(top[0], top[1], u * 0.13, u * 0.065, 0, 0, Math.PI * 2);
  g.fillStyle = "#fff";
  g.fill();
  g.strokeStyle = INK;
  g.lineWidth = s.line;
  g.stroke();
  g.fillStyle = "#fff";
  g.fillRect(top[0] - u * 0.03, top[1] - u * 0.08, u * 0.06, u * 0.075);
  g.strokeRect(top[0] - u * 0.03, top[1] - u * 0.08, u * 0.06, u * 0.075);
  const w = u * 0.085;
  const pelvis = P(-0.06, -0.06, 0.34);
  const skin = s.human ? INK : "#fff";
  // Legs toward the viewer
  limb([P(-0.02, -0.12, 0.34), P(0.16, 0.02, 0.36), P(0.18, 0.04, 0.03)], w * 1.05, s.human ? "#2e2e2e" : "#ececec");
  limb([P(-0.12, -0.02, 0.34), P(0.02, 0.16, 0.36), P(0.04, 0.18, 0.03)], w * 1.05, skin);
  const sl = [pelvis[0] - u * 0.19, pelvis[1] - u * 0.46];
  const sr = [pelvis[0] + u * 0.19, pelvis[1] - u * 0.46];
  g.beginPath();
  g.moveTo(sl[0], sl[1]);
  g.quadraticCurveTo(pelvis[0], sl[1] - u * 0.06, sr[0], sr[1]);
  g.quadraticCurveTo(sr[0] + u * 0.02, pelvis[1] - u * 0.25, pelvis[0] + u * 0.12, pelvis[1]);
  g.lineTo(pelvis[0] - u * 0.12, pelvis[1]);
  g.quadraticCurveTo(sl[0] - u * 0.02, pelvis[1] - u * 0.25, sl[0], sl[1]);
  g.fillStyle = skin;
  g.fill();
  g.save();
  g.clip();
  g.fillStyle = s.human ? "#2e2e2e" : "#ececec";
  g.fillRect(pelvis[0] + u * 0.07, sl[1] - u * 0.1, u * 0.2, u * 0.6);
  g.restore();
  g.strokeStyle = INK;
  g.lineWidth = s.line;
  g.stroke();
  g.beginPath();
  g.moveTo(pelvis[0] - u * 0.1, pelvis[1] - u * 0.33);
  g.quadraticCurveTo(pelvis[0], pelvis[1] - u * 0.27, pelvis[0] + u * 0.1, pelvis[1] - u * 0.33);
  g.strokeStyle = "rgba(25,25,25,0.25)";
  g.stroke();
  const neck = [pelvis[0], pelvis[1] - u * 0.5];
  seg(neck, [neck[0], neck[1] - u * 0.07], u * 0.06, s.human ? INK : "#e6e6e6");
  const c = [pelvis[0], pelvis[1] - u * 0.66];
  if (kind === "lounge-call") {
    const wave = Math.sin(ph * Math.PI * 2) * u * 0.05;
    limb([sl, [sl[0] - u * 0.12, sl[1] + u * 0.22], [sl[0] - u * 0.02 + wave, sl[1] + u * 0.08 - Math.abs(wave)]], w);
    head(s, g, c, "front", ph);
    limb([sr, [sr[0] + u * 0.1, sr[1] + u * 0.14], [c[0] + u * 0.1, c[1] + u * 0.02]], w);
    g.save();
    g.translate(c[0] + u * 0.12, c[1]);
    g.rotate(0.3);
    g.fillStyle = INK;
    g.beginPath();
    if (g.roundRect) g.roundRect(-u * 0.03, -u * 0.09, u * 0.06, u * 0.15, u * 0.015);
    else g.rect(-u * 0.03, -u * 0.09, u * 0.06, u * 0.15);
    g.fill();
    g.restore();
    speech(s, g, [c[0] + u * 0.1, c[1]], ph, 1);
  } else {
    head(s, g, c, "front", ph, "glow");
    const tab = [pelvis[0], pelvis[1] - u * 0.25];
    const tap = Math.sin(ph * Math.PI * 4) * u * 0.02;
    limb([sl, [sl[0] - u * 0.05, sl[1] + u * 0.24], [tab[0] - u * 0.1, tab[1] + u * 0.02]], w, skin);
    g.save();
    g.translate(tab[0], tab[1]);
    g.rotate(-0.12);
    g.fillStyle = s.human ? "#8c8c8c" : INK;
    g.beginPath();
    if (g.roundRect) g.roundRect(-u * 0.14, -u * 0.1, u * 0.28, u * 0.2, u * 0.025);
    else g.rect(-u * 0.14, -u * 0.1, u * 0.28, u * 0.2);
    g.fill();
    g.fillStyle = "rgba(255,255,255,0.35)";
    g.beginPath();
    g.arc(0, 0, u * 0.018, 0, Math.PI * 2);
    g.fill();
    g.restore();
    limb([sr, [sr[0] + u * 0.05, sr[1] + u * 0.24], [tab[0] + u * 0.06, tab[1] - u * 0.04 + tap]], w, skin);
  }
}

// A potted plant for the aisles.
export function drawPlant(g, u, size = 1) {
  const s = makeScene(g, u);
  const { P, box } = s;
  box(-0.1, -0.1, 0, 0.2, 0.2, 0.2, { top: "#d9d9d9", left: "#f4f4f4", right: "#e6e6e6" });
  const leaves = [
    [0, -0.02, 0.62, 0.17],
    [-0.1, 0.06, 0.48, 0.13],
    [0.09, -0.07, 0.5, 0.12],
    [0.02, 0.1, 0.75, 0.11],
    [-0.06, -0.1, 0.78, 0.1],
  ];
  for (const [x, y, z, r] of leaves) {
    const c = P(x, y, z * size);
    g.beginPath();
    g.ellipse(c[0], c[1], r * u * 1.1, r * u * 0.8, -0.5, 0, Math.PI * 2);
    g.fillStyle = "#e9e9e9";
    g.fill();
    g.strokeStyle = INK;
    g.lineWidth = s.line;
    g.stroke();
  }
  s.seg(P(0, 0, 0.2), P(0, 0, 0.5 * size), Math.max(1, u * 0.02), null);
}

// Draws one workstation, tile centre at the current origin. ph is the animation phase, 0-1.
export function drawWorkstation(g, u, kind, ph = 0, { human = false } = {}) {
  const s = makeScene(g, u);
  s.human = human;
  g.save();
  g.lineJoin = "round";
  g.beginPath();
  const { P } = s;
  const shadow = [P(-0.5, -0.5), P(0.5, -0.5), P(0.5, 0.5), P(-0.5, 0.5)];
  poly(g, shadow, "rgba(25,25,25,0.035)");
  if (kind.startsWith("lounge")) loungeWorker(s, g, kind, ph);
  else {
    desk(s, kind === "tower");
    deskItems(s, g, kind, ph);
    deskFront(s);
    chair(s, false);
    deskWorker(s, g, kind, ph);
    chair(s, true);
  }
  g.restore();
}

// One worker with every screen it needs: monitors on two tiers, a laptop, a tablet and a phone.
// Returns each screen's corners so the caller can show which app has the worker's attention.
export const COMMAND_APPS = ["Code", "Video call", "Browser", "Team chat", "Email", "Calendar", "CRM", "Docs", "Analytics"];
export function drawCommandCenter(g, u, ph = 0) {
  const s = makeScene(g, u);
  const { P, box } = s;
  g.save();
  g.lineJoin = "round";
  poly(g, [P(-0.75, -1.2), P(0.95, -1.2), P(0.95, 1.2), P(-0.75, 1.2)], "rgba(25,25,25,0.035)");
  const screens = [];
  // The return of the L: a second computer, a video call and a phone
  box(0.42, -1.02, 0, 0.18, 0.15, 0.36, { top: "#4a4a4a", left: "#333", right: "#2a2a2a" });
  box(-0.1, -1.08, 0.44, 0.9, 0.42, 0.03, DESK);
  box(0.77, -1.08, 0, 0.03, 0.42, 0.44, DESK);
  screens.push(monitor(s, g, 0.1, -0.98, 0.36, "code", (ph + 0.6) % 1, -Math.PI / 2));
  screens.push(monitor(s, g, 0.5, -0.98, 0.36, "video", ph, -Math.PI / 2));
  box(0.58, -0.8, 0.47, 0.06, 0.1, 0.012, { top: INK, left: INK, right: INK });
  if ((ph + 0.25) % 0.5 < 0.25) poly(g, [P(0.59, -0.79, 0.484), P(0.63, -0.79, 0.484), P(0.63, -0.72, 0.484), P(0.59, -0.72, 0.484)], "#fff");
  box(-0.54, -1.08, 0, 0.4, 0.03, 0.44, DESK);
  box(-0.56, -1.08, 0.44, 0.46, 2.16, 0.03, DESK);
  screens.push(monitor(s, g, -0.47, -0.21, 0.4, "browser", ph, -0.22, 0.98, true));
  screens.push(monitor(s, g, -0.47, 0.21, 0.4, "chat", (ph + 0.3) % 1, 0.22, 0.98, true));
  screens.push(monitor(s, g, -0.3, -0.37, 0.38, "mail", (ph + 0.5) % 1, -0.62));
  screens.push(monitor(s, g, -0.4, 0, 0.4, "calendar", ph));
  screens.push(monitor(s, g, -0.3, 0.37, 0.38, "list", (ph + 0.2) % 1, 0.62));
  screens.push(laptop(s, g, -0.2, -0.78, "docs", (ph + 0.7) % 1));
  // A tablet on its stand, and a phone that lights up with each notification
  const tb = [P(-0.3, 0.62, 0.72), P(-0.3, 0.9, 0.72), P(-0.2, 0.9, 0.49), P(-0.2, 0.62, 0.49)];
  s.seg(P(-0.24, 0.76, 0.47), P(-0.26, 0.76, 0.56), Math.max(1, u * 0.024), null);
  poly(g, tb, INK, INK, s.line);
  const inner = [
    [tb[0][0] + (tb[1][0] - tb[0][0]) * 0.07, tb[0][1] + (tb[3][1] - tb[0][1]) * 0.07 + (tb[1][1] - tb[0][1]) * 0.07],
    [tb[1][0] - (tb[1][0] - tb[0][0]) * 0.07, tb[1][1] + (tb[2][1] - tb[1][1]) * 0.07 - (tb[1][1] - tb[0][1]) * 0.07],
    [tb[2][0] - (tb[2][0] - tb[3][0]) * 0.07, tb[2][1] - (tb[2][1] - tb[1][1]) * 0.07 - (tb[2][1] - tb[3][1]) * 0.07],
    [tb[3][0] + (tb[2][0] - tb[3][0]) * 0.07, tb[3][1] - (tb[3][1] - tb[0][1]) * 0.07 + (tb[2][1] - tb[3][1]) * 0.07],
  ];
  poly(g, inner, "#fff");
  s.onQuad(inner[0], inner[1], inner[3], () => CONTENT.chart(g, (ph + 0.4) % 1));
  screens.push(inner);
  box(-0.26, 0.4, 0.47, 0.1, 0.06, 0.012, { top: INK, left: INK, right: INK });
  if (ph % 0.5 < 0.25) poly(g, [P(-0.25, 0.41, 0.484), P(-0.17, 0.41, 0.484), P(-0.17, 0.45, 0.484), P(-0.25, 0.45, 0.484)], "#fff");
  box(-0.24, -0.18, 0.47, 0.09, 0.36, 0.012, { top: "#f2f2f2" });
  box(-0.22, 0.26, 0.47, 0.05, 0.035, 0.012, { top: "#f2f2f2" });
  box(-0.54, 1.05, 0, 0.4, 0.03, 0.44, DESK);
  chair(s, false);
  deskWorker(s, g, "triple", ph);
  chair(s, true);
  g.restore();
  return screens;
}

// A human conductor standing on a podium, facing the viewer, baton raised. beat moves the arms.
export function drawConductor(g, k, beat = 0, flick = 0) {
  const bob = Math.sin(beat * Math.PI * 2);
  g.save();
  g.lineCap = "round";
  g.lineJoin = "round";
  const leg = (dx) => {
    g.beginPath();
    g.moveTo(dx * k * 0.16, -k * 1.55);
    g.lineTo(dx * k * 0.2, -k * 0.05);
    g.strokeStyle = INK;
    g.lineWidth = k * 0.2;
    g.stroke();
  };
  leg(-1);
  leg(1);
  g.beginPath();
  g.moveTo(-k * 0.42, -k * 2.75);
  g.quadraticCurveTo(-k * 0.46, -k * 2.1, -k * 0.28, -k * 1.5);
  g.lineTo(k * 0.28, -k * 1.5);
  g.quadraticCurveTo(k * 0.46, -k * 2.1, k * 0.42, -k * 2.75);
  g.quadraticCurveTo(0, -k * 2.9, -k * 0.42, -k * 2.75);
  g.fillStyle = INK;
  g.fill();
  g.fillStyle = "#3a3a3a";
  g.fillRect(k * 0.12, -k * 2.8, k * 0.3, k * 1.25);
  g.beginPath();
  g.moveTo(-k * 0.08, -k * 2.82);
  g.lineTo(0, -k * 2.5);
  g.lineTo(k * 0.08, -k * 2.82);
  g.fillStyle = "#fff";
  g.fill();
  g.beginPath();
  g.arc(0, -k * 3.18, k * 0.3, 0, Math.PI * 2);
  g.fillStyle = INK;
  g.fill();
  // The baton arm, conducting
  const lift = 0.25 * bob - flick * 0.5;
  const sh = [k * 0.4, -k * 2.68];
  const el = [k * (0.82 + 0.1 * bob), -k * (2.95 + lift)];
  const hand = [k * (1.1 + 0.12 * bob), -k * (3.45 + lift * 1.4)];
  g.beginPath();
  g.moveTo(...sh);
  g.lineTo(...el);
  g.lineTo(...hand);
  g.strokeStyle = INK;
  g.lineWidth = k * 0.16;
  g.stroke();
  const tip = [hand[0] + k * (0.55 - flick * 0.2), hand[1] - k * (0.5 - flick * 0.9)];
  g.beginPath();
  g.moveTo(...hand);
  g.lineTo(...tip);
  g.strokeStyle = INK;
  g.lineWidth = Math.max(1, k * 0.045);
  g.stroke();
  // The open hand, keeping time
  const sl = [-k * 0.4, -k * 2.68];
  const el2 = [-k * (0.8 - 0.08 * bob), -k * (2.9 - 0.2 * bob)];
  const hand2 = [-k * (1.02 - 0.1 * bob), -k * (3.3 - 0.35 * bob)];
  g.beginPath();
  g.moveTo(...sl);
  g.lineTo(...el2);
  g.lineTo(...hand2);
  g.strokeStyle = INK;
  g.lineWidth = k * 0.16;
  g.stroke();
  g.restore();
  return { tip, head: [0, -k * 3.18] };
}

// A team at one shared bench, shoulder to shoulder, with you conducting from a podium at its head.
// Returns where each teammate's head is, and the conductor's, for the relay drawn on top.
const BENCH = [-0.9, -0.3, 0.3, 0.9];
const BENCH_KINDS = [
  ["list", "dual"],
  ["code", "laptop"],
  ["chart", "triple"],
  ["calendar", "call"],
];
export function drawTeamPod(g, u, ph = 0, { beat = 0, flick = 0, flash = 0 } = {}) {
  const s = makeScene(g, u);
  const { P, box } = s;
  g.save();
  g.lineJoin = "round";
  poly(g, [P(-1.35, -1.45), P(0.62, -1.45), P(0.62, 1.45), P(-1.35, 1.45)], "rgba(25,25,25,0.035)");
  // The podium, and you on it
  box(-1.25, -0.2, 0, 0.36, 0.4, 0.52, { top: "#ffffff", left: "#eeeeee", right: "#e0e0e0" });
  const stand = P(-1.07, 0, 0.52);
  g.save();
  g.translate(stand[0], stand[1]);
  const conductor = drawConductor(g, u * 0.34, beat, flick);
  g.restore();
  conductor.tip = [conductor.tip[0] + stand[0], conductor.tip[1] + stand[1]];
  conductor.head = [conductor.head[0] + stand[0], conductor.head[1] + stand[1]];
  // One shared bench
  box(-0.54, -1.3, 0, 0.4, 0.03, 0.44, DESK);
  box(-0.56, -1.3, 0.44, 0.46, 2.6, 0.03, DESK);
  BENCH.forEach((y, k) => {
    const quad = monitor(s, g, -0.4, y, 0.36, BENCH_KINDS[k][0], (ph + k * 0.25) % 1);
    if (flash > 0) poly(g, quad, `rgba(25,25,25,${0.75 * flash})`);
    box(-0.24, y - 0.12, 0.47, 0.08, 0.24, 0.012, { top: "#f2f2f2" });
  });
  box(-0.54, 1.27, 0, 0.4, 0.03, 0.44, DESK);
  const heads = BENCH.map((y, k) => {
    const [dx, dy] = P(0, y, 0);
    g.save();
    g.translate(dx, dy);
    chair(s, false);
    deskWorker(s, g, BENCH_KINDS[k][1] === "triple" ? "triple" : BENCH_KINDS[k][1] === "call" ? "call" : "dual", (ph + k * 0.25) % 1);
    chair(s, true);
    g.restore();
    const hd = P(0.22, y, 0.83);
    return hd;
  });
  g.restore();
  return { heads, conductor };
}

// Four desks pushed together, two facing two: a team working across from each other.
// Returns each teammate's head, in the order the work travels round the pod.
const POD_SEATS = [
  { x: -0.48, y: -0.46, mirror: true, kind: "laptop" },
  { x: -0.48, y: 0.46, mirror: true, kind: "laptop" },
  { x: 0.48, y: -0.46, mirror: false, kind: "dual" },
  { x: 0.48, y: 0.46, mirror: false, kind: "call" },
];
export function drawAgentPod(g, u, ph = 0) {
  const world = makeScene(g, u);
  g.save();
  g.lineJoin = "round";
  poly(g, [world.P(-1.2, -1.0), world.P(1.2, -1.0), world.P(1.2, 1.0), world.P(-1.2, 1.0)], "rgba(25,25,25,0.035)");
  const heads = POD_SEATS.map((seat, k) => {
    const s = makeScene(g, u, seat.mirror);
    const [dx, dy] = world.P(seat.x, seat.y, 0);
    const phase = (ph + k * 0.25) % 1;
    g.save();
    g.translate(dx, dy);
    if (seat.mirror) {
      chair(s, true);
      chair(s, false);
      deskWorker(s, g, seat.kind, phase);
      desk(s, false);
      deskItems(s, g, seat.kind, phase);
      deskFront(s);
    } else {
      desk(s, false);
      deskItems(s, g, seat.kind, phase);
      deskFront(s);
      chair(s, false);
      deskWorker(s, g, seat.kind, phase);
      chair(s, true);
    }
    g.restore();
    const [hx, hy] = s.P(0.22, 0, 0.83);
    return [hx + dx, hy + dy];
  });
  g.restore();
  return [heads[0], heads[1], heads[3], heads[2]];
}

// The working loop, rendered once per kind and mirror so thousands can be drawn as images.
export function workstationSprite(kind, flip, frame, u, dpr = 1) {
  const scale = u * dpr;
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(BOX.width * scale);
  canvas.height = Math.ceil(BOX.height * scale);
  const g = canvas.getContext("2d");
  g.translate(-BOX.left * scale, -BOX.top * scale);
  if (flip) g.scale(-1, 1);
  drawWorkstation(g, scale, kind, frame / FRAMES);
  return canvas;
}

export const PHASE_PERIOD = PERIOD;
